import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear auth and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  requestPasswordReset: async (data) => {
    const response = await api.post('/auth/request-password-reset', data);
    return response.data;
  },
  resetPassword: async (data) => {
    const response = await api.post('/auth/reset-password', data);
    return response.data;
  },
};

// Profile APIs
export const profileAPI = {
  getProfile: async () => {
    const response = await api.get('/profile');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/profile', data);
    return response.data;
  },
  deleteProfile: async () => {
    const response = await api.delete('/profile');
    return response.data;
  },
};

// Chat APIs
export const chatAPI = {
  createSession: async (title = null) => {
    const response = await api.post('/chat/session', title ? { title } : {});
    return response.data;
  },
  updateSessionTitle: async (sessionId, title) => {
    const response = await api.put(`/chat/session/${sessionId}/title`, { title });
    return response.data;
  },
  getSessions: async () => {
    const response = await api.get('/chat/sessions');
    return response.data;
  },
  getMessages: async (sessionId) => {
    const response = await api.get(`/chat/messages/${sessionId}`);
    return response.data;
  },
  sendMessage: async (sessionId, message) => {
    const response = await api.post('/chat/message', {
      session_id: sessionId,
      message: message,
    });
    return response.data;
  },
  sendMessageWithFiles: async (sessionId, message, fileIds) => {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('message', message);
    formData.append('file_ids', JSON.stringify(fileIds));
    
    const response = await api.post('/chat/message/with-files', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  // New streaming version for file analysis
  streamMessageWithFiles: async (sessionId, message, fileIds, callbacks) => {
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('message', message);
    formData.append('file_ids', JSON.stringify(fileIds));
    
    try {
      const response = await fetch(`${API_URL}/api/chat/message/with-files/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'user_message_id':
                  callbacks.onUserMessageId?.(data.id);
                  break;
                case 'status':
                  callbacks.onStatus?.(data.message);
                  break;
                case 'warning':
                  callbacks.onWarning?.(data.message);
                  break;
                case 'chunk':
                  callbacks.onChunk?.(data.content);
                  break;
                case 'complete':
                  callbacks.onComplete?.(data.ai_message_id, data.files_analyzed);
                  break;
                case 'error':
                  callbacks.onError?.(data.message);
                  break;
                default:
                  break;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error('[API] File streaming error:', error);
      callbacks.onError?.(error.message);
      throw error;
    }
  },
  exportToPdf: async (sessionId) => {
    const response = await api.get(`/chat/export-pdf/${sessionId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Medical Records APIs
export const medicalAPI = {
  createRecord: async (recordData) => {
    const response = await api.post('/medical-records', recordData);
    return response.data;
  },
  addRecord: async (formData) => {
    const response = await api.post('/medical-records/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  getRecords: async () => {
    const response = await api.get('/medical-records');
    return response.data;
  },
  deleteRecord: async (recordId) => {
    const response = await api.delete(`/medical-records/${recordId}`);
    return response.data;
  },
  getHealthStats: async () => {
    const response = await api.get('/health-stats');
    return response.data;
  },
  getHealthAnalysis: async () => {
    const response = await api.get('/health-analysis');
    return response.data;
  },
  getHealthMetrics: async (days = 90, system = null) => {
    const params = { days };
    if (system) params.system = system;
    const response = await api.get('/health-metrics', { params });
    return response.data;
  },
  getHealthMetricsGrouped: async (days = 90) => {
    const response = await api.get('/health-metrics/grouped', { params: { days } });
    return response.data;
  },
  deleteAllHealthMetrics: async () => {
    const response = await api.delete('/health-metrics');
    return response.data;
  },
  getMedicalSummary: async () => {
    const response = await api.get('/medical-records/summary');
    return response.data;
  },
  uploadChunk: async (formData) => {
    const response = await api.post('/upload-chunk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  attachFiles: async (recordId, filesData) => {
    const formData = new FormData();
    formData.append('files', JSON.stringify(filesData));
    const response = await api.post(`/attach-files/${recordId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  downloadFile: async (fileId) => {
    const response = await api.get(`/download-file/${fileId}`, {
      responseType: 'blob',
    });
    return response.data;
  },
  // Reviews
  getReviews: async () => {
    const response = await api.get('/reviews');
    return response.data;
  },
  createReview: async (reviewData) => {
    const response = await api.post('/reviews', reviewData);
    return response.data;
  },
};

// RAG API - Advanced Medical AI with Multi-Source Verification
// Fast streaming chat API (new endpoint)
// CHUNK_SIZE: max files per request to avoid infrastructure timeout (~60s)
// 10 files per chunk - each file OCR takes ~3-5s in parallel (batch of 5), so ~10-15s per chunk
const CHUNK_SIZE = 10;

export const fastChatAPI = {
  // Internal helper for single chunk request
  _streamChunk: (sessionId, query, fileIds, isLastChunk, chunkIndex, totalChunks, callbacks, abortSignal) => {
    const { onUserMessageId, onStatus, onChunk, onComplete, onError, onProgress, onSource, onMode, onStep, onDocTotal, onDocStart, onDocDone, onChunkInfo, onCitations, onPlan, onConsiliumSpecialists, onSpecialistStart, onSpecialistDone, onSynthesisStart } = callbacks;
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/chat/stream`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      // Wire AbortSignal -> xhr.abort()
      let abortHandler = null;
      if (abortSignal) {
        if (abortSignal.aborted) {
          xhr.abort();
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        abortHandler = () => {
          try { xhr.abort(); } catch (e) {}
        };
        abortSignal.addEventListener('abort', abortHandler);
      }

      const formData = new FormData();
      formData.append('query', query);
      formData.append('session_id', sessionId);
      formData.append('file_ids', JSON.stringify(fileIds));
      formData.append('is_chunk', !isLastChunk ? 'true' : 'false');
      formData.append('chunk_index', chunkIndex.toString());
      formData.append('total_chunks', totalChunks.toString());

      let lastProcessed = 0;
      let buffer = '';

      const processLine = (line) => {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) return;
        try {
          const data = JSON.parse(trimmed.slice(6));
          switch (data.type) {
            case 'user_message_id': onUserMessageId?.(data.id); break;
            case 'mode': onMode?.(data.value); break;
            case 'step': onStep?.(data); break;
            case 'status': onStatus?.(data.message); break;
            case 'progress': onProgress?.(data.value); break;
            case 'source': onSource?.(data.name); break;
            case 'chunk': onChunk?.(data.content); break;
            case 'complete': onComplete?.(data.ai_message_id, data.files_analyzed); break;
            case 'error': onError?.(data.message); break;
            case 'doc_total': onDocTotal?.(data.count); break;
            case 'doc_start': onDocStart?.(data); break;
            case 'doc_done': onDocDone?.(data); break;
            case 'citations': onCitations?.(data.items); break;
            case 'plan': onPlan?.(data.plan); break;
            case 'consilium_specialists': onConsiliumSpecialists?.(data.items); break;
            case 'specialist_start': onSpecialistStart?.(data); break;
            case 'specialist_done': onSpecialistDone?.(data); break;
            case 'synthesis_start': onSynthesisStart?.(data); break;
            default: break;
          }
        } catch (e) { /* skip invalid JSON */ }
      };

      const processText = (newText) => {
        buffer += newText;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          processLine(line);
        }
      };

      const flushBuffer = () => {
        if (buffer.trim()) {
          processLine(buffer);
          buffer = '';
        }
      };

      xhr.onprogress = () => {
        const fullText = xhr.responseText;
        if (fullText.length > lastProcessed) {
          processText(fullText.slice(lastProcessed));
          lastProcessed = fullText.length;
        }
      };

      xhr.onload = () => {
        const fullText = xhr.responseText;
        if (fullText.length > lastProcessed) {
          processText(fullText.slice(lastProcessed));
        }
        flushBuffer();

        if (abortSignal && abortHandler) abortSignal.removeEventListener('abort', abortHandler);

        if (xhr.status !== 200) {
          const errMsg = `Server error: ${xhr.status}`;
          onError?.(errMsg);
          reject(new Error(errMsg));
          return;
        }
        resolve();
      };

      xhr.onerror = () => {
        if (abortSignal && abortHandler) abortSignal.removeEventListener('abort', abortHandler);
        const errMsg = 'Network error';
        onError?.(errMsg);
        reject(new Error(errMsg));
      };

      xhr.onabort = () => {
        if (abortSignal && abortHandler) abortSignal.removeEventListener('abort', abortHandler);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      xhr.ontimeout = () => {
        if (abortSignal && abortHandler) abortSignal.removeEventListener('abort', abortHandler);
        const errMsg = 'Request timed out';
        onError?.(errMsg);
        reject(new Error(errMsg));
      };

      xhr.timeout = 300000; // 5 minutes per chunk for large file batches
      xhr.send(formData);
    });
  },

  // Main stream function with automatic chunking for large file batches
  stream: async (sessionId, query, fileIds = [], callbacks = {}, abortSignal = null) => {
    const totalFiles = fileIds.length;
    
    // If no files or small batch, process directly
    if (totalFiles <= CHUNK_SIZE) {
      return fastChatAPI._streamChunk(sessionId, query, fileIds, true, 0, 1, callbacks, abortSignal);
    }

    // Split into chunks for large batches
    const chunks = [];
    for (let i = 0; i < totalFiles; i += CHUNK_SIZE) {
      chunks.push(fileIds.slice(i, i + CHUNK_SIZE));
    }
    const totalChunks = chunks.length;

    console.log(`[API] Splitting ${totalFiles} files into ${totalChunks} chunks of max ${CHUNK_SIZE}`);

    // Notify UI about multi-chunk processing
    callbacks.onChunkInfo?.({ totalChunks, totalFiles });
    callbacks.onDocTotal?.(totalFiles);
    callbacks.onMode?.('document');

    let globalDocIndex = 0;
    let allExtractedDocs = [];

    // Process all chunks sequentially
    for (let chunkIdx = 0; chunkIdx < totalChunks; chunkIdx++) {
      const chunkFileIds = chunks[chunkIdx];
      const isLastChunk = chunkIdx === totalChunks - 1;
      const chunkStartIdx = globalDocIndex;

      // Custom callbacks that adjust document indices for global progress
      const chunkCallbacks = {
        ...callbacks,
        onDocStart: (data) => {
          const globalIdx = chunkStartIdx + data.index;
          callbacks.onDocStart?.({
            ...data,
            index: globalIdx,
            total: totalFiles,
            label: `Документ ${globalIdx + 1}/${totalFiles}: ${data.filename?.substring(0, 30) || 'файл'}...`
          });
        },
        onDocDone: (data) => {
          const globalIdx = chunkStartIdx + data.index;
          callbacks.onDocDone?.({
            ...data,
            index: globalIdx
          });
          globalDocIndex++;
        },
        onProgress: (value) => {
          // Scale progress: each chunk covers a portion of 0-90%, final 10% is for LLM
          const chunkWeight = 90 / totalChunks;
          const globalProgress = Math.round(chunkIdx * chunkWeight + (value / 100) * chunkWeight);
          callbacks.onProgress?.(globalProgress);
        },
        onStep: (step) => {
          // Only show steps on last chunk (when LLM is called)
          if (isLastChunk) {
            callbacks.onStep?.(step);
          }
        },
        onChunk: (content) => {
          // Only pass text chunks on last chunk
          if (isLastChunk) {
            callbacks.onChunk?.(content);
          }
        },
        onComplete: (aiMessageId, filesAnalyzed) => {
          // Only emit complete on last chunk
          if (isLastChunk) {
            callbacks.onComplete?.(aiMessageId, filesAnalyzed);
          }
        },
        // Suppress certain events for intermediate chunks
        onDocTotal: isLastChunk ? callbacks.onDocTotal : () => {},
        onMode: () => {}, // Already emitted
      };

      console.log(`[API] Processing chunk ${chunkIdx + 1}/${totalChunks} with ${chunkFileIds.length} files`);
      
      await fastChatAPI._streamChunk(
        sessionId, 
        query, 
        chunkFileIds, 
        isLastChunk, 
        chunkIdx, 
        totalChunks, 
        chunkCallbacks,
        abortSignal
      );
      // If user aborted between chunks, stop processing
      if (abortSignal?.aborted) break;
    }
  }
};

export const ragAPI = {
  // Query with RAG enhancement
  query: async (sessionId, query, fileIds = []) => {
    const formData = new FormData();
    formData.append('query', query);
    formData.append('session_id', sessionId);
    formData.append('file_ids', JSON.stringify(fileIds));
    
    const response = await api.post('/rag/query', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Stream RAG query
  streamQuery: async (sessionId, query, fileIds = [], callbacks = {}) => {
    const { onUserMessageId, onStatus, onChunk, onComplete, onError, onProgress, onSource } = callbacks;
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const formData = new FormData();
    formData.append('query', query);
    formData.append('session_id', sessionId);
    formData.append('file_ids', JSON.stringify(fileIds));
    
    try {
      const response = await fetch(`${API_URL}/api/rag/query/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              switch (data.type) {
                case 'user_message_id':
                  onUserMessageId?.(data.id);
                  break;
                case 'status':
                  onStatus?.(data.message);
                  break;
                case 'progress':
                  onProgress?.(data.value);
                  break;
                case 'source':
                  onSource?.(data.name);
                  break;
                case 'chunk':
                  onChunk?.(data.content);
                  break;
                case 'complete':
                  onComplete?.(data.ai_message_id);
                  break;
                case 'error':
                  onError?.(data.message);
                  break;
                default:
                  break;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error('[RAG API] Streaming error:', error);
      onError?.(error.message);
      throw error;
    }
  },
  
  // Search sources directly
  searchSources: async (query, sourceType = 'all', maxResults = 5) => {
    const response = await api.get('/rag/sources/search', {
      params: { query, source_type: sourceType, max_results: maxResults }
    });
    return response.data;
  },
  
  // Get RAG system health
  health: async () => {
    const response = await api.get('/rag/health');
    return response.data;
  },
  
  // Knowledge Graph endpoints
  graph: {
    searchEntities: async (query) => {
      const response = await api.get('/rag/graph/entities', { params: { query } });
      return response.data;
    },
    
    getDiseaseProfile: async (diseaseName) => {
      const response = await api.get(`/rag/graph/disease/${encodeURIComponent(diseaseName)}`);
      return response.data;
    },
    
    diagnoseBySymptoms: async (symptoms) => {
      const symptomList = Array.isArray(symptoms) ? symptoms.join(',') : symptoms;
      const response = await api.get('/rag/graph/symptoms/diagnose', { 
        params: { symptoms: symptomList } 
      });
      return response.data;
    },
    
    checkDrugInteractions: async (drugs) => {
      const drugList = Array.isArray(drugs) ? drugs.join(',') : drugs;
      const response = await api.get('/rag/graph/drug/interactions', {
        params: { drugs: drugList }
      });
      return response.data;
    },
    
    interpretLabResult: async (testName, value, gender = null) => {
      const formData = new FormData();
      formData.append('test_name', testName);
      formData.append('value', value);
      if (gender) formData.append('gender', gender);
      
      const response = await api.post('/rag/graph/lab/interpret', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    
    getStats: async () => {
      const response = await api.get('/rag/graph/stats');
      return response.data;
    }
  }
};

// MedGemma API - Medical Image Analysis
export const medgemmaAPI = {
  // Analyze a single medical image
  analyzeImage: async (imageFile, options = {}) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('patient_context', options.patientContext || '');
    formData.append('clinical_question', options.clinicalQuestion || '');
    formData.append('language', options.language || 'ru');
    
    const response = await api.post('/medgemma/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 2 minutes timeout for image analysis
    });
    return response.data;
  },
  
  // Analyze multiple images
  analyzeMultiple: async (imageFiles, options = {}) => {
    const formData = new FormData();
    imageFiles.forEach(file => {
      formData.append('images', file);
    });
    formData.append('patient_context', options.patientContext || '');
    formData.append('clinical_question', options.clinicalQuestion || '');
    formData.append('language', options.language || 'ru');
    
    const response = await api.post('/medgemma/analyze/multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000 // 5 minutes for multiple images
    });
    return response.data;
  },
  
  // Get supported modalities
  getModalities: async (language = 'ru') => {
    const response = await api.get('/medgemma/modalities', { params: { language } });
    return response.data;
  },
  
  // Health check
  health: async () => {
    const response = await api.get('/medgemma/health');
    return response.data;
  }
};

// Deep Research API
export const deepResearchAPI = {
  streamResearch: async (query, researchDepth, callbacks) => {
    const { onSessionStarted, onThinking, onSearchQuery, onSourceDiscovered, onResponseChunk, onComplete, onError } = callbacks;
    
    return new Promise((resolve, reject) => {
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const encodedQuery = encodeURIComponent(query);
        const url = `${baseUrl}/api/deep-research/sse?query=${encodedQuery}&depth=${researchDepth || 'medium'}&token=${token}`;
        
        const eventSource = new EventSource(url);
        
        eventSource.onopen = () => {};
        
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
              case 'session_started':
                onSessionStarted?.(data.session_id);
                break;
              case 'thinking':
                onThinking?.(data.content);
                break;
              case 'search_query':
                onSearchQuery?.(data.query);
                break;
              case 'source_discovered':
                onSourceDiscovered?.({
                  index: data.index,
                  uri: data.uri,
                  title: data.title,
                  domain: data.domain,
                  favicon_url: data.favicon_url,
                  source_type: data.source_type,
                });
                break;
              case 'response_chunk':
                onResponseChunk?.(data.content);
                break;
              case 'sources_summary':
                break;
              case 'research_complete':
                onComplete?.(data.session_id);
                eventSource.close();
                resolve();
                break;
              case 'error':
                onError?.(data.message);
                eventSource.close();
                reject(new Error(data.message));
                break;
            }
          } catch (e) {
            console.error('[DeepResearch] Error parsing event:', e);
          }
        };
        
        eventSource.onerror = (error) => {
          console.error('[DeepResearch] EventSource error:', error);
          eventSource.close();
          // Try fallback to fetch
          deepResearchAPI.streamResearchFetch(query, researchDepth, callbacks)
            .then(resolve)
            .catch(reject);
        };
        
      } catch (error) {
        console.error('[DeepResearch] Setup error:', error);
        onError?.(error.message);
        reject(error);
      }
    });
  },
  
  // Fallback fetch-based streaming
  streamResearchFetch: async (query, researchDepth, callbacks) => {
    const { onSessionStarted, onThinking, onSearchQuery, onSourceDiscovered, onResponseChunk, onComplete, onError } = callbacks;
    
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/deep-research/stream`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          query: query,
          research_depth: researchDepth || 'medium',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;
          
          const lines = eventBlock.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                switch (data.type) {
                  case 'session_started':
                    onSessionStarted?.(data.session_id);
                    break;
                  case 'thinking':
                    onThinking?.(data.content);
                    break;
                  case 'search_query':
                    onSearchQuery?.(data.query);
                    break;
                  case 'source_discovered':
                    onSourceDiscovered?.({
                      index: data.index,
                      uri: data.uri,
                      title: data.title,
                      domain: data.domain,
                      favicon_url: data.favicon_url,
                      source_type: data.source_type,
                    });
                    break;
                  case 'response_chunk':
                    onResponseChunk?.(data.content);
                    break;
                  case 'research_complete':
                    onComplete?.(data.session_id);
                    break;
                  case 'error':
                    onError?.(data.message);
                    break;
                }
              } catch (e) {
                console.error('[DeepResearch Fetch] Parse error:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('[DeepResearch Fetch] Error:', error);
      onError?.(error.message);
      throw error;
    }
  },
};

export default api;